// MUST be the first import so registerLicense runs before any Syncfusion
// module is evaluated. Syncfusion's own docs recommend this — see
// https://ej2.syncfusion.com/react/documentation/licensing/license-key-registration/
import "@/lib/registerSyncfusion";
// Loads the saved UI preferences (theme, accent, density…) and keeps <html> in
// step with them; index.html has already applied them before first paint.
import "@/lib/preferences";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "react-oidc-context";
import { TooltipProvider } from "@/components/ui/tooltip";
import { App } from "./App";
import { initSyncfusion } from "@/lib/syncfusion";
import { oidcConfig } from "@/auth/authConfig";
import { StatusAnnouncerProvider } from "@/components/StatusAnnouncer";

import "./styles/globals.css";
// Overrides Syncfusion Fluent2 CSS vars under html.dark so the RTE and
// Schedule follow the app's theme instead of staying light-on-dark.
import "./styles/syncfusion-dark.css";

/** HTTP status of a failed request, from MailApiError.status or the "→ 503" in the message. */
function httpStatus(error: unknown): number | null {
    if (error && typeof error === "object" && "status" in error && typeof (error as { status: unknown }).status === "number") {
        return (error as { status: number }).status;
    }
    const m = error instanceof Error ? /→ (\d{3})/.exec(error.message) : null;
    return m ? Number(m[1]) : null;
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            // A 503 is the BFF saying "mailbox briefly unreachable, try again"
            // (e.g. while Identity restarts during a deploy): retry it quietly
            // for ~30 s, honouring its Retry-After of 5 s. Everything else keeps
            // TanStack's default 3 retries with exponential back-off.
            retry: (failureCount, error) => (httpStatus(error) === 503 ? failureCount < 6 : failureCount < 3),
            retryDelay: (attempt, error) => (httpStatus(error) === 503 ? 5_000 : Math.min(1000 * 2 ** attempt, 30_000))
        }
    }
});

const rootEl = document.getElementById("root");
if (!rootEl) {
    throw new Error("Root element #root not found in index.html");
}

// Kicks off a fetch to the BFF and calls registerLicense again with the
// server-side copy as a safety net. The side-effect import above has
// already registered the `.env.local` copy — this just ensures we pick up
// any backend-only overrides in prod.
void initSyncfusion();

ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
        <AuthProvider {...oidcConfig}>
            <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                    <BrowserRouter>
                        <StatusAnnouncerProvider>
                            <App />
                        </StatusAnnouncerProvider>
                    </BrowserRouter>
                </TooltipProvider>
            </QueryClientProvider>
        </AuthProvider>
    </React.StrictMode>
);
